import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { clamp } from '@/lib/dom';

/**
 * The rubber band: press on empty space, sweep, and everything the rectangle
 * touches is selected — live, while you sweep, exactly as XP did it.
 *
 * The band is painted by whoever uses the hook, inside their own container, so
 * a band drawn in a folder window is clipped by that window rather than
 * escaping across the desktop.
 */

export interface Band {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface MarqueeOptions {
  /** The box the band is drawn in and clamped to. */
  boundsRef: RefObject<HTMLElement | null>;
  /** The band has just started; snapshot whatever selection it will build on. */
  onStart?(additive: boolean): void;
  /** One frame of the sweep. The rectangle is in client coordinates. */
  onSweep(rect: Band, additive: boolean): void;
  onEnd?(): void;
}

export interface Marquee {
  /** The band to paint, in the bounding element's own coordinates. */
  band: Band | null;
  onPointerDown(event: ReactPointerEvent): void;
}

/** Below this a sweep is a click that wobbled. */
const THRESHOLD = 4;

function boxBetween(ax: number, ay: number, bx: number, by: number): Band {
  return {
    left: Math.min(ax, bx),
    top: Math.min(ay, by),
    width: Math.abs(ax - bx),
    height: Math.abs(ay - by),
  };
}

export function useMarquee(options: MarqueeOptions): Marquee {
  const [band, setBand] = useState<Band | null>(null);
  const optionsRef = useRef(options);
  const frameRef = useRef<number | null>(null);

  // Written after commit rather than during render, so a discarded render can
  // never leave a live sweep calling into callbacks that never shipped.
  useEffect(() => {
    optionsRef.current = options;
  });

  const onPointerDown = useCallback((event: ReactPointerEvent) => {
    if (event.button !== 0) return;
    const bounds = optionsRef.current.boundsRef.current;
    if (!bounds) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const additive = event.ctrlKey || event.metaKey || event.shiftKey;

    let sweeping = false;
    let prevUserSelect = '';
    let pending: { x: number; y: number } | null = null;

    const flush = () => {
      frameRef.current = null;
      if (!pending) return;
      const { x, y } = pending;
      pending = null;

      const box = bounds.getBoundingClientRect();
      const cx = clamp(x, box.left, box.right);
      const cy = clamp(y, box.top, box.bottom);
      const client = boxBetween(startX, startY, cx, cy);

      setBand({
        left: client.left - box.left,
        top: client.top - box.top,
        width: client.width,
        height: client.height,
      });
      optionsRef.current.onSweep(client, additive);
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== event.pointerId) return;
      if (!sweeping) {
        if (Math.abs(e.clientX - startX) < THRESHOLD && Math.abs(e.clientY - startY) < THRESHOLD) {
          return;
        }
        sweeping = true;
        prevUserSelect = document.body.style.userSelect;
        document.body.style.userSelect = 'none';
        optionsRef.current.onStart?.(additive);
      }
      pending = { x: e.clientX, y: e.clientY };
      if (frameRef.current === null) frameRef.current = requestAnimationFrame(flush);
    };

    const finish = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', finish);
      window.removeEventListener('blur', finish);

      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      if (!sweeping) return;
      document.body.style.userSelect = prevUserSelect;
      setBand(null);
      optionsRef.current.onEnd?.();
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', finish);
    document.addEventListener('pointercancel', finish);
    window.addEventListener('blur', finish);
  }, []);

  // A surface unmounted mid-sweep leaves a frame queued and nothing to paint.
  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  return { band, onPointerDown };
}

/** Do two client-space rectangles touch? */
export function bandHits(band: Band, rect: DOMRect): boolean {
  return (
    rect.right >= band.left &&
    rect.left <= band.left + band.width &&
    rect.bottom >= band.top &&
    rect.top <= band.top + band.height
  );
}
